import { getCurrentUsage } from "@/lib/usageGate";

// The trial mark: one small "Created with FreeDailyPro.com" badge (logo and name) per PDF page (bottom-right)
// and two on images (top-right and bottom-right), on finished PDFs and images
// that come out of a trial (not paid, not admin) session. Never in the middle
// of the page. It is a pointer back to the site and a reason to upgrade.
// Everything runs in the browser; nothing is sent anywhere.
//
// What is marked: PDFs (footer line with a link) and PNG/JPEG/WebP images
// (corner label), including the PDFs and images packed inside a ZIP the tool builds. What is never marked: text, CSV, JSON, code, audio, video,
// EPUB and other data files, and tools whose output must stay exact
// (QR codes, barcodes, icons, compression, password handling, EXIF, format
// conversion). Change the two lists below to change that policy.

export const TRIAL_MARK_TEXT = "FreeDailyPro";
export const TRIAL_MARK_URL = "https://freedailypro.com";
/** The words in the badge, drawn as ONE text run (so copy/extract reads it as a sentence). Also the link's alt text, which is how an already-marked PDF is recognised. */
export const TRIAL_MARK_LABEL = "Created with FreeDailyPro.com";
const MAX_MARK_BYTES = 60 * 1024 * 1024;
const MAX_IMAGE_PIXELS = 40_000_000;

/** Tool ids whose PDF output must stay byte-faithful or is about protection. */
const PDF_SKIP = /compress|password|protect|unlock|decrypt|repair|redact|sign|linearize|pdf-a\b|bookmark|bates|exhibit/;
/** Tool ids whose image output must stay exact or is used as a functional asset. */
const IMAGE_SKIP = /qr|barcode|favicon|icon|exif|compress|convert|heic|svg|ocr|scan|sprite|resize|crop|rotate|flip/;

/** Tool ids whose pictures are small profile-style images: one smaller badge, bottom-right only, instead of two. */
const SINGLE_BADGE_IMAGE = /^avatar/;

export type MarkKind = "pdf" | "image" | null;

export function markKindFor(mime: string, filename: string): MarkKind {
  const m = (mime || "").toLowerCase();
  const n = filename.toLowerCase();
  if (m === "application/pdf" || n.endsWith(".pdf")) return "pdf";
  if (m === "image/png" || m === "image/jpeg" || m === "image/webp" || /\.(png|jpe?g|webp)$/.test(n)) return "image";
  return null;
}

export function currentToolId(): string {
  if (typeof window === "undefined") return "";
  const m = window.location.pathname.match(/^\/tool\/([^/?#]+)/);
  return m ? m[1] : "";
}

/** True when this session is a trial one: usage is known, and it is neither paid nor admin. */
export function isTrialSession(): boolean {
  const u = getCurrentUsage();
  if (!u) return false;
  if (u.isPro || u.isAdmin) return false;
  return true;
}

/** Would a download of this file get the mark right now? */
export function wouldMark(mime: string, filename: string, toolId = currentToolId()): boolean {
  if (!isTrialSession()) return false;
  const kind = markKindFor(mime, filename);
  if (!kind) return false;
  if (kind === "pdf" && PDF_SKIP.test(toolId)) return false;
  if (kind === "image" && IMAGE_SKIP.test(toolId)) return false;
  return true;
}

/** Tool ids that build ZIPs out of the person's own files (nothing of ours to mark inside). */
const ZIP_SKIP = /^(zip-unzip|file-archive|archive|extract-from-powerpoint|extract-charts-powerpoint|split-powerpoint)/;

function isZipName(filename: string, mime: string): boolean {
  return /\.zip$/i.test(filename) || (mime || "").toLowerCase() === "application/zip";
}

/** Would a ZIP download be opened to mark the PDFs and images inside it? (A ZIP with nothing markable comes out unchanged.) */
export function zipMayBeMarked(blob: Blob, filename: string, toolId = currentToolId()): boolean {
  return isTrialSession() && isZipName(filename, blob.type) && !ZIP_SKIP.test(toolId) && blob.size <= MAX_MARK_BYTES;
}

/** Marks every PDF and image inside a ZIP the same way a single download would be marked. Returns the original on any problem. */
export async function markZipForTrial(blob: Blob, toolId = currentToolId()): Promise<Blob> {
  try {
    const { default: JSZip } = await import("jszip");
    const zip = await JSZip.loadAsync(await blob.arrayBuffer());
    let changed = false;
    for (const entry of Object.values(zip.files)) {
      if (entry.dir) continue;
      const kind = markKindFor("", entry.name);
      if (!kind || (kind === "pdf" && PDF_SKIP.test(toolId)) || (kind === "image" && IMAGE_SKIP.test(toolId))) continue;
      const bytes = await entry.async("uint8array");
      const type = kind === "pdf" ? "application/pdf" : /\.png$/i.test(entry.name) ? "image/png" : /\.webp$/i.test(entry.name) ? "image/webp" : "image/jpeg";
      const typed = new Blob([bytes as BlobPart], { type });
      const marked = kind === "pdf" ? await markPdf(typed) : await markImage(typed, entry.name, toolId);
      if (marked !== typed) { zip.file(entry.name, new Uint8Array(await marked.arrayBuffer())); changed = true; }
    }
    return changed ? new Blob([await zip.generateAsync({ type: "uint8array", compression: "DEFLATE" }) as BlobPart], { type: "application/zip" }) : blob;
  } catch {
    return blob;
  }
}

/** Returns the marked copy, or the original blob if marking is not allowed or fails. Never throws. */
export async function markBlobForTrial(blob: Blob, filename: string, toolId = currentToolId()): Promise<Blob> {
  try {
    if (zipMayBeMarked(blob, filename, toolId)) return await markZipForTrial(blob, toolId);
    if (blob.size > MAX_MARK_BYTES || !wouldMark(blob.type, filename, toolId)) return blob;
    const kind = markKindFor(blob.type, filename);
    if (kind === "pdf") return await markPdf(blob);
    if (kind === "image") return await markImage(blob, filename);
  } catch {
    /* a download must never fail because of the mark */
  }
  return blob;
}


// ---- Badge geometry, shared by the PDF and image paths -------------------
// Local coordinates: origin bottom-left of the badge, y up, height H.
const NAVY = "#111a2b";
const TEAL = "#4fd8c4";
const WHITE = "#ffffff";

interface Badge {
  w: number;
  h: number;
  /** pill: x, y, w, h (fully rounded ends) */
  rects: Array<{ x: number; y: number; w: number; h: number; color: string }>;
  dot: { cx: number; cy: number; r: number; color: string };
  texts: Array<{ text: string; x: number; y: number; size: number; color: string }>;
}

function layoutBadge(H: number, measure: (t: string, size: number) => number): Badge {
  const padL = 0.36 * H;
  const S = 0.5 * H; // logo box
  const k = S / 32.5; // LogoMark units -> local
  const lx = padL;
  const ly = (H - S) / 2;
  const fy = (yDown: number) => ly + (52 - yDown) * k; // logo y-down -> local y-up
  const fx = (xr: number) => lx + (xr - 22) * k;
  const rects = [
    { x: fx(22), y: fy(52), w: 7 * k, h: 32 * k, color: TEAL },
    { x: fx(22), y: fy(27), w: 21 * k, h: 7 * k, color: TEAL },
    { x: fx(22), y: fy(40), w: 16 * k, h: 7 * k, color: TEAL },
  ];
  const dot = { cx: fx(50), cy: fy(48), r: 4.5 * k, color: TEAL };
  const size = 0.4 * H;
  const gap = 0.24 * H;
  let x = lx + S + gap;
  const ty = H / 2 - size * 0.33;
  const texts: Badge["texts"] = [];
  // One run in one colour: split runs made text extraction read "F / reeDailyPro".
  texts.push({ text: TRIAL_MARK_LABEL, x, y: ty, size, color: WHITE });
  x += measure(TRIAL_MARK_LABEL, size);
  const w = x + 0.42 * H;
  return { w, h: H, rects, dot, texts };
}

const hex = (c: string) => [parseInt(c.slice(1, 3), 16) / 255, parseInt(c.slice(3, 5), 16) / 255, parseInt(c.slice(5, 7), 16) / 255] as const;

/** True when the first page already carries our badge link (its alt text is the label; no hidden metadata). */
function pdfHasMark(pdf: import("pdf-lib").PDFDocument, PDFName: typeof import("pdf-lib").PDFName): boolean {
  try {
    const annots = pdf.getPages()[0]?.node.Annots();
    if (!annots) return false;
    for (let i = 0; i < annots.size(); i++) {
      const d = annots.lookup(i) as unknown as { get(k: unknown): unknown } | undefined;
      const c = d?.get?.(PDFName.of("Contents")) as { decodeText?: () => string } | undefined;
      if (c?.decodeText?.() === TRIAL_MARK_LABEL) return true;
    }
  } catch {
    /* treat as unmarked */
  }
  return false;
}

export async function markPdf(blob: Blob): Promise<Blob> {
  const { PDFDocument, StandardFonts, rgb, PDFName, PDFString, PDFArray, pushGraphicsState, popGraphicsState, concatTransformationMatrix } = await import("pdf-lib");
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const pdf = await PDFDocument.load(bytes, { updateMetadata: false });
  if (pdfHasMark(pdf, PDFName)) return blob; // already marked
  const font = await pdf.embedFont(StandardFonts.HelveticaBold);
  const col = (c: string) => { const [r, g, b] = hex(c); return rgb(r, g, b); };
  for (const page of pdf.getPages()) {
    // A page with nothing on it (a blank page a tool inserted on purpose, such as Merge's "blank page after odd
    // files") stays truly blank: it has no content stream at all.
    if (!page.node.has(PDFName.of("Contents"))) continue;
    const mb = page.getCropBox(); // the visible area (the media box when there is no crop box), so a cropped page still shows the badge
    const rot = ((page.getRotation().angle % 360) + 360) % 360;
    const dw = rot === 90 || rot === 270 ? mb.height : mb.width; // size as displayed
    const dh = rot === 90 || rot === 270 ? mb.width : mb.height;
    // display (X right, Y up) -> page space
    const m = rot === 90 ? [0, 1, -1, 0, mb.width, 0] : rot === 180 ? [-1, 0, 0, -1, mb.width, mb.height] : rot === 270 ? [0, -1, 1, 0, 0, mb.height] : [1, 0, 0, 1, 0, 0];
    const toPage = (X: number, Y: number): [number, number] => [m[0] * X + m[2] * Y + m[4] + mb.x, m[1] * X + m[3] * Y + m[5] + mb.y];
    const H = Math.min(20, Math.max(12, Math.min(dw, dh) * 0.034));
    const badge = layoutBadge(H, (t, size) => font.widthOfTextAtSize(t, size));
    const margin = Math.max(8, H * 0.7);
    const bx = dw - badge.w - margin;
    const spots = [margin]; // one badge per page: bottom-right
    for (const by of spots) {
      page.pushOperators(pushGraphicsState(), concatTransformationMatrix(m[0], m[1], m[2], m[3], m[4] + mb.x, m[5] + mb.y));
      const r = H / 2;
      page.drawRectangle({ x: bx + r, y: by, width: badge.w - H, height: H, color: col(NAVY) });
      page.drawCircle({ x: bx + r, y: by + r, size: r, color: col(NAVY) });
      page.drawCircle({ x: bx + badge.w - r, y: by + r, size: r, color: col(NAVY) });
      for (const q of badge.rects) page.drawRectangle({ x: bx + q.x, y: by + q.y, width: q.w, height: q.h, color: col(q.color) });
      page.drawCircle({ x: bx + badge.dot.cx, y: by + badge.dot.cy, size: badge.dot.r, color: col(badge.dot.color) });
      for (const t of badge.texts) page.drawText(t.text, { x: bx + t.x, y: by + t.y, size: t.size, font, color: col(t.color) });
      page.pushOperators(popGraphicsState());
      try {
        const c1 = toPage(bx, by);
        const c2 = toPage(bx + badge.w, by + H);
        const ctx = pdf.context;
        const link = ctx.obj({
          Type: "Annot",
          Subtype: "Link",
          Rect: [Math.min(c1[0], c2[0]), Math.min(c1[1], c2[1]), Math.max(c1[0], c2[0]), Math.max(c1[1], c2[1])],
          Border: [0, 0, 0],
          Contents: PDFString.of(TRIAL_MARK_LABEL),
          A: { Type: "Action", S: "URI", URI: PDFString.of(TRIAL_MARK_URL) },
        });
        const ref = ctx.register(link);
        const existing = page.node.lookup(PDFName.of("Annots"));
        if (existing instanceof PDFArray) existing.push(ref);
        else page.node.set(PDFName.of("Annots"), ctx.obj([ref]));
      } catch {
        /* the badge alone is fine */
      }
    }
  }
  const out = await pdf.save({ useObjectStreams: true });
  return new Blob([out as BlobPart], { type: "application/pdf" });
}

export async function markImage(blob: Blob, filename: string, toolId = currentToolId()): Promise<Blob> {
  if (typeof document === "undefined" || typeof createImageBitmap === "undefined") return blob;
  const bmp = await createImageBitmap(blob);
  const { width, height } = bmp;
  if (width * height > MAX_IMAGE_PIXELS || width < 160 || height < 100) { bmp.close?.(); return blob; }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) { bmp.close?.(); return blob; }
  ctx.drawImage(bmp, 0, 0);
  bmp.close?.();
  const single = SINGLE_BADGE_IMAGE.test(toolId);
  const H = single ? Math.max(16, Math.round(Math.min(width, height) * 0.04)) : Math.max(22, Math.round(Math.min(width, height) * 0.055));
  const family = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
  const measure = (t: string, size: number) => { ctx.font = `700 ${size}px ${family}`; return ctx.measureText(t).width; };
  const badge = layoutBadge(H, measure);
  const margin = Math.round(H * 0.6);
  const bx = width - badge.w - margin;
  const spots = single ? [height - H - margin] : [margin, height - H - margin]; // canvas y is down: top-right, bottom-right
  const r = H / 2;
  for (const topY of spots) {
    const flip = (y: number, h = 0) => topY + H - y - h; // local y-up -> canvas y-down
    ctx.globalAlpha = 0.94;
    ctx.fillStyle = NAVY;
    ctx.beginPath();
    ctx.moveTo(bx + r, topY);
    ctx.arcTo(bx + badge.w, topY, bx + badge.w, topY + H, r);
    ctx.arcTo(bx + badge.w, topY + H, bx, topY + H, r);
    ctx.arcTo(bx, topY + H, bx, topY, r);
    ctx.arcTo(bx, topY, bx + badge.w, topY, r);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1;
    for (const q of badge.rects) { ctx.fillStyle = q.color; ctx.fillRect(bx + q.x, flip(q.y, q.h), q.w, q.h); }
    ctx.fillStyle = badge.dot.color;
    ctx.beginPath();
    ctx.arc(bx + badge.dot.cx, flip(badge.dot.cy), badge.dot.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.textBaseline = "alphabetic";
    for (const t of badge.texts) { ctx.font = `700 ${t.size}px ${family}`; ctx.fillStyle = t.color; ctx.fillText(t.text, bx + t.x, flip(t.y)); }
  }
  const type = /\.png$/i.test(filename) || blob.type === "image/png" ? "image/png" : blob.type === "image/webp" ? "image/webp" : "image/jpeg";
  const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, type, 0.92));
  return out ?? blob;
}


// ---- SVG pictures -----------------------------------------------------------
// An SVG a tool builds itself (the avatar) cannot be stamped after the fact like a PNG, so the same single
// small badge is added to the SVG text. Same rule as the PNG: trial sessions only, same tools.

/** Would an SVG this tool exports get the badge right now? */
export function wouldMarkSvg(toolId = currentToolId()): boolean {
  return isTrialSession() && !IMAGE_SKIP.test(toolId);
}

/** The badge as SVG elements (one `<g>`), placed bottom-right of a vbW x vbH picture. Pure; used by markSvgText. */
export function trialBadgeSvg(vbW: number, vbH: number): string {
  const H = Math.round(Math.min(vbW, vbH) * 0.04 * 100) / 100;
  const badge = layoutBadge(H, (t, size) => t.length * size * 0.6);
  const margin = H * 0.6;
  const bx = vbW - badge.w - margin;
  const top = vbH - H - margin;
  const n = (v: number) => Math.round(v * 100) / 100;
  const flip = (y: number, h = 0) => top + H - y - h;
  const parts: string[] = [`<g id="fdp-trial-mark" font-family="system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif" font-weight="700">`];
  parts.push(`<rect x="${n(bx)}" y="${n(top)}" width="${n(badge.w)}" height="${n(H)}" rx="${n(H / 2)}" fill="${NAVY}" fill-opacity="0.94"/>`);
  for (const q of badge.rects) parts.push(`<rect x="${n(bx + q.x)}" y="${n(flip(q.y, q.h))}" width="${n(q.w)}" height="${n(q.h)}" fill="${q.color}"/>`);
  parts.push(`<circle cx="${n(bx + badge.dot.cx)}" cy="${n(flip(badge.dot.cy))}" r="${n(badge.dot.r)}" fill="${badge.dot.color}"/>`);
  for (const t of badge.texts) parts.push(`<text x="${n(bx + t.x)}" y="${n(flip(t.y))}" font-size="${n(t.size)}" fill="${t.color}" xml:space="preserve">${t.text}</text>`);
  parts.push("</g>");
  return parts.join("");
}

/** Adds the badge just before the closing </svg>. Returns the text unchanged when there is no closing tag. */
export function markSvgText(svg: string, vbW: number, vbH: number): string {
  const i = svg.lastIndexOf("</svg>");
  if (i < 0 || svg.includes('id="fdp-trial-mark"')) return svg;
  return svg.slice(0, i) + trialBadgeSvg(vbW, vbH) + svg.slice(i);
}
